export type AgenticProductionCanaryStatus =
  | 'not_started'
  | 'local_proof_ready'
  | 'production_env_missing'
  | 'production_readback_pending'
  | 'production_canary_passed'
  | 'cleanup_required'
  | 'failed_closed'

export type AgenticProductionCanaryTone = 'success' | 'warning' | 'destructive' | 'muted'

export type AgenticProductionCanaryEvidence = Record<string, unknown>

export interface AgenticProductionCanaryFact {
  label: string
  value: string
  missing?: boolean
}

export interface AgenticProductionCanaryModel {
  status: AgenticProductionCanaryStatus
  label: string
  tone: AgenticProductionCanaryTone
  summary: string
  facts: AgenticProductionCanaryFact[]
  missing: string[]
  failureReasons: string[]
}

export const AGENTIC_CANARY_ENV_NAMES = [
  'COGO_STUDIO_PRODUCTION_SESSION',
  'COGO_STUDIO_BASE_URL',
  'COGO_STUDIO_SESSION_TOKEN',
] as const

export const AGENTIC_CANARY_REQUIRED_BOUNDARIES = [
  'routeStructuralProof',
  'browserRenderProof',
  'designSystemVariantProof',
  'applicationDiversity',
  'referoStyleKnowledge',
  'productionStudioCanary',
] as const

export const AGENTIC_CANARY_STORAGE_PREFIX = 'cogo-studio:agentic-production-canary:v1:'

const EMBEDDED_EVIDENCE_KEYS = [
  'agenticProductionCanaryEvidence',
  'productionStudioCanaryEvidence',
  'productionCanaryEvidence',
  'agenticRagFinalProofEvidence',
  'agenticFinalProofEvidence',
  'productionStudioReadbackEvidence',
] as const

export function agenticCanaryStorageKey(projectId: string | null | undefined): string {
  return `${AGENTIC_CANARY_STORAGE_PREFIX}${projectId || 'unscoped'}`
}

export function parseAgenticProductionCanaryEvidence(input: string):
  | { ok: true; evidence: AgenticProductionCanaryEvidence }
  | { ok: false; error: string } {
  try {
    const parsed = JSON.parse(input)
    const evidence = asRecord(parsed)
    if (!evidence) {
      return { ok: false, error: 'Check data must be a JSON object.' }
    }
    return { ok: true, evidence: sanitizeEvidenceRecord(evidence) }
  } catch {
    return { ok: false, error: 'Check data must be valid JSON.' }
  }
}

export function findAgenticProductionCanaryEvidence(...sources: unknown[]): AgenticProductionCanaryEvidence | null {
  for (const source of sources) {
    const direct = pickEvidence(source)
    if (direct) return direct

    const record = asRecord(source)
    const blocks = asRecord(record?.blocks)
    if (!blocks) continue

    for (const block of Object.values(blocks)) {
      const blockRecord = asRecord(block)
      const metadataEvidence = pickEvidence(blockRecord?.metadata)
      if (metadataEvidence) return metadataEvidence
    }
  }
  return null
}

export function buildAgenticProductionCanaryModel(
  evidence: AgenticProductionCanaryEvidence | null | undefined,
): AgenticProductionCanaryModel {
  if (!evidence) {
    return {
      status: 'not_started',
      label: 'not started',
      tone: 'muted',
      summary: 'No live verification evidence has been imported for this Studio project yet.',
      facts: buildFacts({}),
      missing: [
        'live Studio verification evidence',
        'source: production-studio-readback',
        'temporary project cleanup proof',
      ],
      failureReasons: [],
    }
  }

  const extracted = extractEvidenceFields(evidence)
  const missing = buildMissingList(extracted)
  const failureReasons = buildFailureReasons(extracted)
  const envMissing = extracted.missingEnvVars.length > 0
  const readbackReady = extracted.hasProductionReadback && extracted.hasRequiredReadbackFacts
  const allBoundariesPass = extracted.requiredBoundaries.every((boundary) => boundary.pass)
  const cleanupComplete = extracted.cleanupComplete

  if (envMissing) {
    return {
      status: 'production_env_missing',
      label: 'env missing',
      tone: 'warning',
      summary: 'Production canary is blocked until the required Studio production env names are present.',
      facts: buildFacts(extracted),
      missing,
      failureReasons,
    }
  }

  if (failureReasons.length > 0) {
    return {
      status: 'failed_closed',
      label: 'failed closed',
      tone: 'destructive',
      summary: 'The imported evidence contains a fail-closed condition and should not be treated as production-ready.',
      facts: buildFacts(extracted),
      missing,
      failureReasons,
    }
  }

  if (!allBoundariesPass || !readbackReady) {
    return {
      status: 'production_readback_pending',
      label: 'verification pending',
      tone: 'warning',
      summary: 'Local proof may be ready, but a production pass still requires signed-in Studio verification evidence.',
      facts: buildFacts(extracted),
      missing,
      failureReasons,
    }
  }

  if (!cleanupComplete) {
    return {
      status: 'cleanup_required',
      label: 'cleanup required',
      tone: 'warning',
      summary: 'Live verification passed, but the temporary Studio project cleanup proof is not complete.',
      facts: buildFacts(extracted),
      missing,
      failureReasons,
    }
  }

  return {
    status: 'production_canary_passed',
    label: 'production canary passed',
    tone: 'success',
    summary: 'Production Studio canary has passing verification, provenance, payload hash, and cleanup evidence.',
    facts: buildFacts(extracted),
    missing: [],
    failureReasons: [],
  }
}

interface BoundaryStatus {
  name: string
  status: string | null
  pass: boolean
}

interface ExtractedEvidenceFields {
  baseUrl?: string
  createdProjectId?: string
  readbackEndpoint?: string
  readbackStatus?: string
  payloadHash?: string
  provenance?: string
  cleanupStatus?: string
  manualCleanupProjectId?: string
  cleanupComplete: boolean
  hasProductionReadback: boolean
  hasRequiredReadbackFacts: boolean
  missingEnvVars: string[]
  requiredBoundaries: BoundaryStatus[]
  explicitFailureReasons: string[]
}

function extractEvidenceFields(evidence: AgenticProductionCanaryEvidence): ExtractedEvidenceFields {
  const readback = firstRecord(
    evidence.readback,
    evidence.productionReadback,
    evidence.productionStudioReadback,
    evidence.result && asRecord(evidence.result)?.readback,
  )
  const cleanup = firstRecord(evidence.cleanup, evidence.productionCleanup, evidence.manualCleanup)
  const boundaries = firstRecord(
    evidence.proofBoundary,
    evidence.proofBoundaries,
    evidence.boundaries,
    evidence.proofs,
    evidence.result && asRecord(evidence.result)?.proofBoundary,
  )
  const env = firstRecord(evidence.env, evidence.environment, evidence.productionEnv)

  const createdProjectId = firstString(
    evidence.createdProjectId,
    evidence.projectId,
    evidence.studioProjectId,
    readback?.projectId,
    cleanup?.projectId,
  )
  const manualCleanupProjectId = firstString(
    cleanup?.manualCleanupProjectId,
    cleanup?.exactProjectId,
    cleanup?.projectId,
    evidence.manualCleanupProjectId,
  )
  const provenance = firstString(
    evidence.source,
    evidence.provenance,
    evidence.provenanceMarker,
    readback?.source,
    readback?.provenance,
    readback?.provenanceMarker,
  )
  const readbackStatus = firstStringOrNumber(
    readback?.status,
    readback?.httpStatus,
    readback?.statusCode,
    evidence.readbackStatus,
    evidence.readbackHttpStatus,
  )
  const payloadHash = firstString(readback?.payloadHash, readback?.hash, evidence.payloadHash, evidence.readbackPayloadHash)
  const cleanupStatus = firstString(cleanup?.status, cleanup?.result, evidence.cleanupStatus)
  const baseUrl = firstString(evidence.baseUrl, evidence.productionStudioBaseUrl, evidence.studioBaseUrl, readback?.baseUrl)
  const readbackEndpoint = firstString(
    readback?.endpoint,
    readback?.url,
    readback?.path,
    evidence.readbackEndpoint,
  )

  const requiredBoundaries = AGENTIC_CANARY_REQUIRED_BOUNDARIES.map((name) => {
    const status = normalizeStatus(boundaries?.[name] ?? evidence[name])
    return { name, status, pass: isPassStatus(status) }
  })

  const missingEnvVars = uniqueStrings([
    ...stringArray(evidence.missingEnvVars),
    ...stringArray(env?.missing),
    ...stringArray(env?.missingVars),
    ...collectReasonStrings(evidence)
      .filter((reason) => AGENTIC_CANARY_ENV_NAMES.some((envName) => reason.includes(envName))),
  ])

  const cleanupComplete = isPassStatus(normalizeStatus(cleanupStatus))
    || Boolean(createdProjectId && manualCleanupProjectId && manualCleanupProjectId === createdProjectId)
  const hasProductionReadback = Boolean(provenance?.includes('production-studio-readback'))
  const hasRequiredReadbackFacts = Boolean(
    createdProjectId
      && readbackEndpoint
      && isExpectedReadbackEndpoint(readbackEndpoint)
      && isPassStatus(normalizeStatus(readbackStatus))
      && payloadHash,
  )

  return {
    baseUrl,
    createdProjectId,
    readbackEndpoint,
    readbackStatus,
    payloadHash,
    provenance,
    cleanupStatus,
    manualCleanupProjectId,
    cleanupComplete,
    hasProductionReadback,
    hasRequiredReadbackFacts,
    missingEnvVars,
    requiredBoundaries,
    explicitFailureReasons: collectFailureStrings(evidence, requiredBoundaries),
  }
}

function buildFacts(fields: Partial<ExtractedEvidenceFields>): AgenticProductionCanaryFact[] {
  const cleanupValue = fields.cleanupComplete
    ? fields.cleanupStatus || `manual cleanup recorded for ${fields.manualCleanupProjectId}`
    : fields.cleanupStatus || fields.manualCleanupProjectId || undefined

  return [
    fact('Studio base URL', fields.baseUrl),
    fact('Temporary project id', fields.createdProjectId),
    fact('Verification endpoint', fields.readbackEndpoint),
    fact('Verification status', fields.readbackStatus),
    fact('Payload hash', fields.payloadHash),
    fact('Provenance marker', fields.provenance),
    fact('Cleanup proof', cleanupValue),
  ]
}

function buildMissingList(fields: ExtractedEvidenceFields): string[] {
  const missing = [
    ...fields.requiredBoundaries
      .filter((boundary) => !boundary.pass)
      .map((boundary) => `${boundary.name}: pass`),
    !fields.hasProductionReadback ? 'source: production-studio-readback' : null,
    !fields.createdProjectId ? 'temporary production Studio project id' : null,
    !fields.readbackEndpoint || !isExpectedReadbackEndpoint(fields.readbackEndpoint)
      ? '/api/projects/{projectId}/full verification endpoint'
      : null,
    !fields.readbackStatus || !isPassStatus(normalizeStatus(fields.readbackStatus)) ? 'passing verification status' : null,
    !fields.payloadHash ? 'verification payload hash' : null,
    !fields.cleanupComplete ? 'temporary project cleanup proof' : null,
    ...fields.missingEnvVars.map((envName) => `env: ${envName}`),
  ]
  return uniqueStrings(missing.filter((item): item is string => Boolean(item)))
}

function buildFailureReasons(fields: ExtractedEvidenceFields): string[] {
  if (fields.missingEnvVars.length > 0) return []
  return fields.explicitFailureReasons
}

function pickEvidence(source: unknown): AgenticProductionCanaryEvidence | null {
  const record = asRecord(source)
  if (!record) return null
  if (hasCanarySignature(record)) return record

  for (const key of EMBEDDED_EVIDENCE_KEYS) {
    const candidate = asRecord(record[key])
    if (candidate) return candidate
  }

  const metadata = asRecord(record.metadata)
  if (!metadata) return null
  if (hasCanarySignature(metadata)) return metadata
  for (const key of EMBEDDED_EVIDENCE_KEYS) {
    const candidate = asRecord(metadata[key])
    if (candidate) return candidate
  }
  return null
}

function hasCanarySignature(record: Record<string, unknown>): boolean {
  return Boolean(
    record.productionStudioCanary
      || record.productionReadback
      || record.productionStudioReadback
      || asRecord(record.boundaries)?.productionStudioCanary
      || asRecord(record.proofBoundary)?.productionStudioCanary
      || firstString(asRecord(record.readback)?.source, asRecord(record.readback)?.provenance)?.includes(
        'production-studio-readback',
      )
      || firstString(record.source, record.provenance, record.provenanceMarker)?.includes('production-studio-readback'),
  )
}

function collectFailureStrings(evidence: AgenticProductionCanaryEvidence, boundaries: BoundaryStatus[]): string[] {
  const boundaryFailures = boundaries
    .filter((boundary) => boundary.status && !isPassStatus(boundary.status) && !isPendingStatus(boundary.status))
    .map((boundary) => `${boundary.name}: ${boundary.status}`)

  return uniqueStrings([
    ...stringArray(evidence.failClosedReasons),
    ...stringArray(evidence.failedClosedReasons),
    ...stringArray(evidence.errors),
    ...stringArray(evidence.error),
    ...collectReasonStrings(evidence).filter((reason) => /fail|error|regress/i.test(reason)),
    ...boundaryFailures,
  ])
}

function collectReasonStrings(evidence: AgenticProductionCanaryEvidence): string[] {
  return uniqueStrings([
    ...stringArray(evidence.blockedReasons),
    ...stringArray(evidence.remainingGaps),
    ...stringArray(evidence.reasons),
    ...stringArray(asRecord(evidence.result)?.blockedReasons),
    ...stringArray(asRecord(evidence.result)?.remainingGaps),
  ])
}

function isPendingStatus(status: string | null): boolean {
  if (!status) return true
  return /pending|defer|missing|not_started|not started|blocked/i.test(status)
}

function isPassStatus(status: string | null): boolean {
  if (!status) return false
  return /^(pass|passed|ok|success|succeeded|complete|completed|cleaned|deleted|ready|true|2\d\d)$/.test(status)
}

function isExpectedReadbackEndpoint(endpoint: string): boolean {
  return endpoint.includes('/api/projects/') && endpoint.includes('/full')
}

function normalizeStatus(value: unknown): string | null {
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'string') return value.trim().toLowerCase()
  const record = asRecord(value)
  return firstString(record?.status, record?.result, record?.state)?.trim().toLowerCase() ?? null
}

function firstRecord(...values: unknown[]): Record<string, unknown> | undefined {
  for (const value of values) {
    const record = asRecord(value)
    if (record) return record
  }
  return undefined
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
  return value as Record<string, unknown>
}

function sanitizeEvidenceRecord(record: Record<string, unknown>): AgenticProductionCanaryEvidence {
  return Object.fromEntries(
    Object.entries(record).map(([key, value]) => [key, sanitizeEvidenceValue(key, value)]),
  )
}

function sanitizeEvidenceValue(key: string, value: unknown): unknown {
  if (isSensitiveEvidenceKey(key)) return '[redacted]'
  if (Array.isArray(value)) return value.map((item) => sanitizeEvidenceValue('', item))
  const record = asRecord(value)
  if (!record) return value
  return sanitizeEvidenceRecord(record)
}

function isSensitiveEvidenceKey(key: string): boolean {
  return /^(COGO_STUDIO_SESSION_TOKEN|COGO_STUDIO_PRODUCTION_SESSION)$/i.test(key)
    || /(session[_-]?token|access[_-]?token|refresh[_-]?token|auth[_-]?token|secret|password|pwd|authorization|cookie|api[_-]?key)$/i.test(key)
}

function firstString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return undefined
}

function firstStringOrNumber(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  }
  return undefined
}

function stringArray(value: unknown): string[] {
  if (typeof value === 'string' && value.trim()) return [value.trim()]
  if (!Array.isArray(value)) return []
  return value
    .flatMap((item) => {
      if (typeof item === 'string' && item.trim()) return [item.trim()]
      const record = asRecord(item)
      return firstString(record?.message, record?.reason, record?.name) ?? []
    })
    .filter((item): item is string => typeof item === 'string' && item.length > 0)
}

function uniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values))
}

function fact(label: string, value: string | undefined): AgenticProductionCanaryFact {
  return value ? { label, value } : { label, value: 'missing', missing: true }
}
